import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import {
	EMlpIconTypes,
	EMplVariantTypes,
	MplIconMessage,
	MplRadioButtonWithDates,
} from '@mopla/ui';

import type { IValidity } from '../types';
import {
	formatActiveFrom,
	getValidUntilParts,
	IValidityConfig,
} from '../validity/validityLabels';

interface IValidityContentProps {
	config: IValidityConfig;
	selectedStartDate: string | null;
	validity: IValidity;
	onSelectStartDate: (iso: string) => void;
	onOpenPicker: () => void;
}

export const ValidityContent: FC<IValidityContentProps> = ({
	config,
	selectedStartDate,
	validity,
	onSelectStartDate,
	onOpenPicker,
}) => {
	const { t } = useTranslation('ticketshop');

	if (config.mode === 'static' && config.staticKeys) {
		return (
			<Stack gap={1} sx={{ width: '100%' }}>
				<MplIconMessage
					type={EMlpIconTypes.Information}
					variant={EMplVariantTypes.Default}
					message={t(config.staticKeys.description)}
					textVariant="l1"
				/>
				<MplIconMessage
					type={EMlpIconTypes.Information}
					variant={EMplVariantTypes.Info}
					message={t(config.staticKeys.note)}
					textVariant="l1"
				/>
			</Stack>
		);
	}
	// options mode
	const presetDates = config.options
		.filter((o) => !o.opensPicker)
		.map((o) => o.startDate);
	const isPickSelected =
		selectedStartDate !== null && !presetDates.includes(selectedStartDate);

	let validUntilLabel: string | null = null;
	if (selectedStartDate) {
		const { date, time } = getValidUntilParts(selectedStartDate, validity);
		validUntilLabel = time
			? t('ticket_detail.validity_valid_until', { date, time })
			: t('ticket_detail.validity_valid_until_no_time', { date });
	}

	return (
		<Stack gap={1} sx={{ width: '100%' }}>
			{config.options.map((option) => {
				const checked = option.opensPicker
					? isPickSelected
					: selectedStartDate === option.startDate;

				const isUnselectedPick =
					!!option.opensPicker && !(isPickSelected && selectedStartDate);
				const activeFromLabel =
					isUnselectedPick && option.pickHintKey
						? t(option.pickHintKey)
						: t('ticket_detail.validity_active_from_label');
				const activeFromDate = option.opensPicker
					? isPickSelected && selectedStartDate
						? formatActiveFrom(selectedStartDate)
						: ''
					: formatActiveFrom(option.startDate);

				const isPickedPick =
					!!option.opensPicker && isPickSelected && !!selectedStartDate;
				const text = isPickedPick
					? t('ticket_detail.validity_pick_date_edit')
					: t(option.labelKey, option.labelParams);

				return (
					<MplRadioButtonWithDates
						key={option.id}
						text={text}
						ticketActiveFromLabel={activeFromLabel}
						ticketActiveFromDate={activeFromDate}
						validityTextColor="solutions.600"
						ticketValidUntilLabel=""
						ticketValidUntilDate=""
						compact
						checked={checked}
						onChange={() =>
							option.opensPicker
								? onOpenPicker()
								: onSelectStartDate(option.startDate)
						}
					/>
				);
			})}
			{validUntilLabel && (
				<Typography variant="l1" color="primary.main">
					{validUntilLabel}
				</Typography>
			)}
		</Stack>
	);
};
